import { useContext, useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Alert, Button, Checkbox, Spinner, TextField } from '@shared';
import { AuthContext } from '@/features/auth/auth-context';
import { useTranslations } from '@/i18n/client';
import type { UserApi } from '@/services/api/user-api';

import { useUserProfileSettings } from './useUserProfileSettings';

interface PersonalSettingsFormData {
  firstName: string;
  lastName: string;
  notifyByMail: boolean;
}

export interface PersonalSettingsSectionProps {
  /** Test-only injection point for a mock UserApi. */
  user?: UserApi;
  embedded?: boolean;
}

export function PersonalSettingsSection({ user, embedded = false }: PersonalSettingsSectionProps) {
  const auth = useContext(AuthContext);
  const { data, loading, loadError, saving, saveError, saved, save, clearSaveError } =
    useUserProfileSettings(user);
  const t = useTranslations('settings');
  const tStatus = useTranslations('common.status');
  const tErrors = useTranslations('errors');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PersonalSettingsFormData>();

  useLayoutEffect(() => {
    if (!data) return;

    reset({
      firstName: data.firstName,
      lastName: data.lastName,
      notifyByMail: data.notifyByMail,
    });
  }, [data, reset]);

  const sectionClassName = embedded ? 'py-8' : 'md-dashboard__card';

  async function onSubmit(formData: PersonalSettingsFormData): Promise<void> {
    clearSaveError();
    const request = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      notifyByMail: formData.notifyByMail,
    };
    const succeeded = await save(request);

    if (succeeded && auth?.user) {
      auth.updateUser({
        ...auth.user,
        firstname: request.firstName,
        lastname: request.lastName,
      });
    }
  }

  if (loading) {
    return (
      <section className={sectionClassName}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Spinner label={t('personal.title')} />
        </div>
      </section>
    );
  }

  if (loadError) {
    return (
      <section className={sectionClassName}>
        <Alert variant="error">{tErrors(`api.${loadError}`)}</Alert>
      </section>
    );
  }

  return (
    <section className={sectionClassName}>
      <h2 className={embedded ? 'm-0 mb-1.5 text-[1.35rem]' : undefined}>{t('personal.title')}</h2>
      <p className={embedded ? 'm-0 mb-6 leading-[1.6] text-[var(--zs-muted)]' : undefined}>
        {t('personal.subtitle')}
      </p>

      {saveError ? <Alert variant="error">{tErrors(`api.${saveError}`)}</Alert> : null}
      {saved ? <Alert variant="success">{t('personal.savedMessage')}</Alert> : null}

      <form className="md-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <fieldset disabled={saving} style={{ border: 'none', padding: 0, margin: 0 }}>
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="sm:flex-1">
              <TextField
                label={t('personal.firstNameLabel')}
                type="text"
                {...register('firstName', { required: t('personal.errors.firstNameRequired') })}
                message={errors.firstName?.message}
                invalid={Boolean(errors.firstName)}
                required
              />
            </div>
            <div className="sm:flex-1">
              <TextField
                label={t('personal.lastNameLabel')}
                type="text"
                {...register('lastName', { required: t('personal.errors.lastNameRequired') })}
                message={errors.lastName?.message}
                invalid={Boolean(errors.lastName)}
                required
              />
            </div>
          </div>

          <Checkbox label={t('personal.notifyByMailLabel')} {...register('notifyByMail')} />

          <Button className="mt-4" type="submit" loading={saving} loadingLabel={tStatus('working')}>
            {t('personal.saveButton')}
          </Button>
        </fieldset>
      </form>
    </section>
  );
}
